import { type ComponentProps, useEffect } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { useHtmlFormInputs } from '../../hooks/useHtmlFormInputs/useHtmlFormInputs';

export type ProvideDetailsInputs = {
    subcategory: string | number | null;
    billing_answer: string | number;
    name: string;
    deal_name_template: string | number;
    description: string;
};

export type FormDetailsProviderProps = {
    errors?: string;
    inputs: ReturnType<typeof useHtmlFormInputs>;
} & ComponentProps<any>;

export function FormDetailsProvider({
    errors: errorsJson,
    inputs,
    children,
}: FormDetailsProviderProps) {
    const form = useForm<ProvideDetailsInputs>({
        mode: 'all',
        defaultValues: {
            subcategory: inputs.subcategory.defaultValue,
            billing_answer: inputs.billing_answer.defaultValue,
            name: inputs.name.defaultValue,
            deal_name_template: inputs.deal_name_template.defaultValue,
            description: inputs.description.defaultValue,
        },
    });

    const onSubmit: SubmitHandler<ProvideDetailsInputs> = () => {
        const externalForm = document.querySelector('form.fast-form') as HTMLFormElement;
        const uploaderStatus = document.querySelector('[data-uploader-status]');
        if (
            uploaderStatus !== null &&
            uploaderStatus.getAttribute('data-uploader-status') === 'true'
        ) {
            externalForm.submit();
        } else {
            document.querySelector('.uploader__dropbox')?.scrollIntoView({
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        try {
            const errors: Partial<Record<keyof ProvideDetailsInputs, string[]>> = JSON.parse(
                errorsJson ?? '{}',
            );
            const validKeys: Array<keyof ProvideDetailsInputs> = [
                'subcategory',
                'billing_answer',
                'name',
                'deal_name_template',
                'description',
            ];
            Object.entries(errors).forEach(([key, value]) => {
                if (
                    validKeys.includes(key as keyof ProvideDetailsInputs) &&
                    value !== undefined &&
                    value?.length > 0
                ) {
                    form.setError(key as keyof ProvideDetailsInputs, {
                        type: 'custom',
                        message: value[0],
                    });
                }
            });
        } catch (e) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            console.log(`Error during parsing json: ${errorsJson}`);
        }
    }, []);

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit) as any}>{children}</form>
        </FormProvider>
    );
}
